import { ApolloProvider } from "@apollo/client/react";
import { GetConsentStatusForRedirect } from "__generated__/types";
//import { DevRoutes } from "common/DevRoutes";
import { AuthProvider, useAuth } from "common/auth/Auth";
import { Loading } from "common/components/Loading";
import { ProtectedRoute } from "common/components/ProtectedRoute";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { ExtendedStudyProgrammesProvider } from "common/context/ExtendedStudyProgrammesContext";
import { MyStudyProgrammesProvider } from "common/context/MyStudyProgrammesContext";
import { NavigationProvider } from "common/context/Navigation";
import { SidebarStateProvider } from "common/context/SidebarContext";
import { createClient } from "common/graphql/Apollo";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { getLocalStorageValue } from "common/local-storage";
import { urls } from "common/urls";
import { AdminRoutes } from "domain/admin/AdminRoutes";
import {
  ArticlePage,
  FeedbackPage,
  FolderPage,
  KurssloeyfeElementPage,
  KurssloeyfePage,
  LibraryPage,
  MultipleChoicePage,
  MyStudies,
  ProfilePage,
  QuestionsPage,
  SelfAssessmentElementPage,
  SelfAssessmentPage,
  SimulationPage,
  StudyProgrammePage,
  TeacherFeedbackPage,
  TeacherQuestionsPage,
} from "domain/pages";
import { ExamSyllabusPage } from "domain/pages/ExamSyllabusPage";
import { TermsOfUsePage } from "domain/pages/TermsOfUsePage";
import { consentStatusFragment } from "domain/profile/ConsentForm";
import gql from "graphql-tag";
import { HTML5toTouch } from "rdndmb-html5-to-touch";
import React, { ReactNode } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import { Navigate, Route, Routes } from "react-router-dom";
import { PreviousPathProvider } from "../common/context/PreviousPathContext";
import { FrontPage } from "./front-page/FrontPage";
import { SimulationCasePage } from "./pages/SimulationCasePage";

import { DevRoutes } from "common/DevRoutes";
import { TERMS_VERSION } from "domain/pages/TermsOfUsePage";

// We code-split GraphiQL since it's dependency tree is very large
// and it is only used by developers.
//
//
const GraphiqlPage = React.lazy(
  () => import(/* webpackChunkName: "graphiql" */ "./graphiql/GraphiqlPage"),
);

const GET_CONSENT_STATUS = gql`
  query GetConsentStatusForRedirect {
    consentStatusList {
      ...ConsentStatusFragment
    }
  }
  ${consentStatusFragment}
`;

const client = createClient();

const AppRoutes: React.FC = () => {
  const { me } = useAuth();
  const { data } =
    useSimpleQuery<GetConsentStatusForRedirect>(GET_CONSENT_STATUS);

  const isNotAdmin = !me?.menu.graphiql;
  const shouldCompleteConsent = data?.consentStatusList.some(
    (consent) => !consent.completed,
  );

  const seenTerm = getLocalStorageValue({
    key: "termsVersion",
    defaultValue: null,
  });

  return (
    <>
      {me?.principal &&
        isNotAdmin &&
        (seenTerm != TERMS_VERSION || shouldCompleteConsent) && (
          <Navigate to={urls.termsOfUse} />
        )}
      <Routes>
        <Route
          path={urls.frontPage}
          element={
            <MyStudyProgrammesProvider>
              <FrontPage />
            </MyStudyProgrammesProvider>
          }
        />
        <Route
          path={urls.profile}
          element={
            <ProtectedRoute
              element={
                <ExtendedStudyProgrammesProvider>
                  <ProfilePage />
                </ExtendedStudyProgrammesProvider>
              }
              authz={() => true}
            />
          }
        />
        <Route
          path={urls.profilePrivacy}
          element={
            <ProtectedRoute
              element={
                <MyStudyProgrammesProvider>
                  <ProfilePage />
                </MyStudyProgrammesProvider>
              }
              authz={() => true}
            />
          }
        />
        <Route
          path={urls.profileStudies}
          element={
            <ProtectedRoute
              element={
                <ExtendedStudyProgrammesProvider>
                  <ProfilePage />
                </ExtendedStudyProgrammesProvider>
              }
              authz={() => true}
            />
          }
        />
        <Route
          path={urls.termsOfUse}
          element={
            <ProtectedRoute element={<TermsOfUsePage />} authz={() => true} />
          }
        />
        <Route
          path={urls.myStudies}
          element={
            <ProtectedRoute
              element={<MyStudies />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.simulation}
          element={
            <ProtectedRoute
              element={<SimulationPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />

        <Route
          path={urls.simulationCase(":caseId")}
          element={
            <ProtectedRoute
              element={<SimulationCasePage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.library}
          element={
            <ProtectedRoute element={<LibraryPage />} authz={() => true} />
          }
        />
        <Route
          path={urls.exam(":examId")}
          element={
            <ProtectedRoute
              element={<ExamSyllabusPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.examDescription(":examId")}
          element={
            <ProtectedRoute
              element={<ExamSyllabusPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.folder(":folderId")}
          element={
            <ProtectedRoute
              element={<FolderPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.folderDescription(":folderId")}
          element={
            <ProtectedRoute
              element={<FolderPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.studyProgramme(":studyProgrammeId")}
          element={
            <ProtectedRoute
              element={<StudyProgrammePage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.studyProgrammeDescription(":studyProgrammeId")}
          element={
            <ProtectedRoute
              element={<StudyProgrammePage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.article(":articleId")}
          element={
            <ProtectedRoute
              element={<ArticlePage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.question(":multipleChoiceId")}
          element={
            <ProtectedRoute
              element={<MultipleChoicePage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.kurssloeyfeArticle(
            ":kurssloeyfeElementId",
            ":kurssloeyfeId",
          )}
          element={
            <ProtectedRoute
              element={<KurssloeyfeElementPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />

        <Route
          path={urls.kurssloeyfeQuestion(
            ":kurssloeyfeElementId",
            ":kurssloeyfeId",
          )}
          element={
            <ProtectedRoute
              element={<KurssloeyfeElementPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />

        <Route
          path={urls.kurssloeyfe(":kurssloeyfeId")}
          element={
            <ProtectedRoute
              element={<KurssloeyfePage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.selfAssessmentArticle(
            ":selfAssessmentElementId",
            ":selfAssessmentId",
          )}
          element={
            <ProtectedRoute
              element={<SelfAssessmentElementPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.selfAssessmentQuestion(
            ":selfAssessmentElementId",
            ":selfAssessmentId",
          )}
          element={
            <ProtectedRoute
              element={<SelfAssessmentElementPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.selfAssessment(":selfAssessmentInstanceId")}
          element={
            <ProtectedRoute
              element={<SelfAssessmentPage />}
              authz={(auth) => auth.me?.menu.studies === true}
            />
          }
        />
        <Route
          path={urls.feedback}
          element={
            <ProtectedRoute
              element={<FeedbackPage />}
              authz={(auth) => auth.me?.menu.feedback === true}
            />
          }
        />
        <Route
          path={urls.questions}
          element={
            <ProtectedRoute
              element={<QuestionsPage />}
              authz={(auth) => auth.me?.menu.feedback === true}
            />
          }
        />
        <Route
          path={urls.teacherFeedback}
          element={
            <ProtectedRoute
              element={<TeacherFeedbackPage />}
              authz={(auth) => auth.me?.menu.teacherFeedback === true}
            />
          }
        />
        <Route
          path={urls.teacherQuestions}
          element={
            <ProtectedRoute
              element={<TeacherQuestionsPage />}
              authz={(auth) => auth.me?.menu.teacherFeedback === true}
            />
          }
        />
        {
          <Route
            path={urls.graphiql}
            element={
              <ProtectedRoute
                element={<GraphiqlPage />}
                authz={(auth) => auth.me?.menu.graphiql === true}
              />
            }
          />
        }
        <Route
          path={urls.cognitoCallback}
          element={
            <LoadingLayout>
              <Loading />
            </LoadingLayout>
          }
        />
        <Route
          path="/*"
          element={
            <ExtendedStudyProgrammesProvider>
              <AdminRoutes />
            </ExtendedStudyProgrammesProvider>
          }
        />
        <Route path="/*" element={<DevRoutes />} />
      </Routes>
    </>
  );
};

const Dnd = ({ children }: { children: ReactNode }) => {
  return <DndProvider options={HTML5toTouch}>{children}</DndProvider>;
};

const InnerApp: React.FC = () => {
  return (
    <PreviousPathProvider>
      <NavigationProvider>
        <SidebarStateProvider>
          <ApolloProvider client={client}>
            <AuthProvider>
              <Dnd>
                {/* TODO: Improve fallback. */}
                <React.Suspense
                  fallback={
                    <LoadingLayout>
                      <Loading />
                    </LoadingLayout>
                  }
                >
                  <AppRoutes />
                </React.Suspense>
              </Dnd>
            </AuthProvider>
          </ApolloProvider>
        </SidebarStateProvider>
      </NavigationProvider>
    </PreviousPathProvider>
  );
};

export const App = InnerApp;
